import React, { useState, useMemo } from 'react';

// --- Icons using Lucide React and Custom SVG ---

// Custom INR Icon (Indian Rupee Symbol)
const RupeeIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3h12a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3H6"/>
        <path d="M6 11h12a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3H6"/>
        <line x1="6" y1="3" x2="6" y2="21"/>
        <line x1="12" y1="3" x2="12" y2="21"/>
        <line x1="6" y1="7" x2="18" y2="7"/>
    </svg>
);
// Fallback/Placeholder icons (simulated import)
const LayoutGridIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3"/><rect width="7" height="7" x="14" y="3"/><rect width="7" height="7" x="14" y="14"/><rect width="7" height="7" x="3" y="14"/></svg>;
const ListTodoIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="4" rx="1"/><path d="M7 9v11"/><path d="M17 9v11"/><path d="M12 9v11"/><path d="M7 3v2"/><path d="M17 3v2"/><path d="M12 3v2"/></svg>;
const SendIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>;
const CheckIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>;

// --- Styles and Global Setup ---

const primaryColor = 'blue-600';
const primaryHover = 'blue-700';
const primaryBg = 'blue-50';

/**
 * Custom Toggle Switch Component
 */
const ToggleSwitch = ({ label, checked, onChange }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 transition-shadow hover:shadow-sm font-roboto">
        <label className="text-sm font-medium text-gray-700 cursor-pointer">{label}</label>
        <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input
                type="checkbox"
                name="toggle"
                id={`toggle-${label.replace(/\s/g, '-')}`}
                checked={checked}
                onChange={onChange}
                className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-2 border-gray-300 appearance-none cursor-pointer checked:right-0 checked:border-blue-600"
            />
            <label htmlFor={`toggle-${label.replace(/\s/g, '-')}`} className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${checked ? 'bg-blue-500' : 'bg-gray-200'}`}></label>
        </div>
        <style jsx="true">{`
            .toggle-checkbox {
                left: 0;
                top: 0;
                transition: left 0.2s ease-in-out, background-color 0.2s ease-in-out;
            }
            .toggle-checkbox:checked {
                left: 1.25rem;
            }
        `}</style>
    </div>
);

/**
 * Step 1: Job Details
 */
const JobDetailsStep = ({ formData, handleChange, nextStep }) => (
    <div className="space-y-5 font-roboto">
        <h2 className="text-2xl font-poppins font-semibold text-gray-800 border-b border-blue-100 pb-3 mb-4">1. Basic Job Information</h2>

        {/* Job Title */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="title" className="text-sm font-medium text-gray-700">Job Title</label>
            <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Senior Software Engineer"
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
            />
        </div>

        {/* Location */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="location" className="text-sm font-medium text-gray-700">Location</label>
            <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Bangalore, India or Remote"
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
            />
        </div>

        {/* Remote Toggle */}
        <ToggleSwitch
            label="Allow Remote Work"
            checked={formData.isRemote}
            onChange={(e) => handleChange({ target: { name: 'isRemote', value: e.target.checked } })}
        />

        {/* Job Description (Simulated Rich Text Editor) */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="description" className="text-sm font-medium text-gray-700">Job Description</label>
            <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="6"
                placeholder="Provide a detailed description of the role, responsibilities, and team culture."
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 resize-none shadow-sm"
            />
        </div>

        {/* Smaller, right-aligned button */}
        <div className="flex justify-end pt-3">
            <button onClick={nextStep} className={`bg-${primaryColor} text-white font-semibold py-2 px-6 rounded-lg hover:bg-${primaryHover} transition duration-150 shadow-md text-sm font-poppins`}>
                Save Details & Continue
            </button>
        </div>
    </div>
);

/**
 * Step 2: Compensation
 */
const CompensationStep = ({ formData, handleChange, nextStep, prevStep }) => (
    <div className="space-y-5 font-roboto">
        <h2 className="text-2xl font-poppins font-semibold text-gray-800 border-b border-blue-100 pb-3 mb-4">2. Compensation & Benefits</h2>

        {/* Salary Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col space-y-1">
                <label htmlFor="salaryMin" className="text-sm font-medium text-gray-700">Min Annual Salary (INR)</label>
                <input
                    type="number"
                    id="salaryMin"
                    name="salaryMin"
                    value={formData.salaryMin}
                    onChange={handleChange}
                    placeholder="Min CTC"
                    className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
                />
            </div>
            <div className="flex flex-col space-y-1">
                <label htmlFor="salaryMax" className="text-sm font-medium text-gray-700">Max Annual Salary (INR)</label>
                <input
                    type="number"
                    id="salaryMax"
                    name="salaryMax"
                    value={formData.salaryMax}
                    onChange={handleChange}
                    placeholder="Max CTC"
                    className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
                />
            </div>
        </div>

        {/* Benefits */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="benefits" className="text-sm font-medium text-gray-700">Key Benefits Offered</label>
            <textarea
                id="benefits"
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                rows="4"
                placeholder="e.g., Health Insurance, Gym Stipend, Unlimited PTO, Stock Options (Use bullet points here)"
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 resize-none shadow-sm"
            />
        </div>

        <div className="flex justify-end space-x-3 pt-3">
            <button onClick={prevStep} className="border border-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg hover:bg-gray-100 transition duration-150 text-sm font-poppins">
                Back
            </button>
            <button onClick={nextStep} className={`bg-${primaryColor} text-white font-semibold py-2 px-6 rounded-lg hover:bg-${primaryHover} transition duration-150 shadow-md text-sm font-poppins`}>
                Continue to Requirements
            </button>
        </div>
    </div>
);

/**
 * Step 3: Requirements
 */
const RequirementsStep = ({ formData, handleChange, nextStep, prevStep }) => (
    <div className="space-y-5 font-roboto">
        <h2 className="text-2xl font-poppins font-semibold text-gray-800 border-b border-blue-100 pb-3 mb-4">3. Candidate Requirements</h2>

        {/* Experience Level */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="experience" className="text-sm font-medium text-gray-700">Experience Level</label>
            <select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 bg-white shadow-sm"
            >
                <option value="">Select Level</option>
                <option value="Entry">Entry Level (0-2 years)</option>
                <option value="Mid">Mid Level (3-5 years)</option>
                <option value="Senior">Senior Level (6+ years)</option>
                <option value="Lead">Lead/Management</option>
            </select>
        </div>

        {/* Required Skills (Simulated Tag Input) */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="skills" className="text-sm font-medium text-gray-700">Required Skills (Comma Separated)</label>
            <input
                type="text"
                id="skills"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                placeholder="e.g., React, NodeJS, SQL, AWS"
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
            />
        </div>

        {/* Educational Qualification */}
        <div className="flex flex-col space-y-1">
            <label htmlFor="education" className="text-sm font-medium text-gray-700">Minimum Education</label>
            <input
                type="text"
                id="education"
                name="education"
                value={formData.education}
                onChange={handleChange}
                placeholder="e.g., B.Tech/B.E. in Computer Science"
                className="p-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-300 focus:border-blue-500 transition duration-150 shadow-sm"
            />
        </div>

        <div className="flex justify-end space-x-3 pt-3">
            <button onClick={prevStep} className="border border-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg hover:bg-gray-100 transition duration-150 text-sm font-poppins">
                Back
            </button>
            <button onClick={nextStep} className={`bg-${primaryColor} text-white font-semibold py-2 px-6 rounded-lg hover:bg-${primaryHover} transition duration-150 shadow-md text-sm font-poppins`}>
                Final Review & Publish
            </button>
        </div>
    </div>
);

/**
 * Step 4: Final Review & Publish
 */
const PublishStep = ({ formData, prevStep, handleSubmit }) => (
    <div className="space-y-5 font-roboto">
        <h2 className="text-2xl font-poppins font-semibold text-gray-800 border-b border-blue-100 pb-3 mb-4">4. Review & Publish</h2>

        <div className="bg-gray-50 p-5 rounded-xl space-y-4 border border-blue-100 shadow-inner">
            <h3 className="text-xl font-poppins font-bold text-blue-700">{formData.title}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="font-medium text-gray-700">Location:</span> {formData.location} {formData.isRemote && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-1 font-poppins font-medium">Remote</span>}</p>
                <p><span className="font-medium text-gray-700">Experience:</span> {formData.experience} Level</p>
                <p><span className="font-medium text-gray-700">Salary Range:</span> <span className='text-green-600'>₹{formData.salaryMin.toLocaleString()} - ₹{formData.salaryMax.toLocaleString()}</span></p>
                <p><span className="font-medium text-gray-700">Education:</span> {formData.education}</p>
            </div>
            <div className="text-sm text-gray-600 pt-3 border-t border-blue-100">
                <span className="font-medium text-gray-700 block mb-1">Description Snippet:</span>
                {formData.description.substring(0, 150)}...
            </div>
        </div>

        {/* Final Checks */}
        <div className="space-y-3 pt-4">
            <h4 className="text-lg font-poppins font-semibold text-gray-700">Publish Options</h4>
            <ToggleSwitch label="Auto-Share to LinkedIn" checked={true} onChange={() => {}} />
            <ToggleSwitch label="Require Cover Letter" checked={false} onChange={() => {}} />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
            <button onClick={prevStep} className="border border-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg hover:bg-gray-100 transition duration-150 text-sm font-poppins">
                Back to Requirements
            </button>
            <button onClick={handleSubmit} className="bg-green-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-green-700 transition duration-150 shadow-md flex items-center justify-center text-sm font-poppins">
                <SendIcon className="w-4 h-4 mr-2"/> Publish Job Now
            </button>
        </div>
    </div>
);

/**
 * Sidebar Step Navigation Component
 */
const Sidebar = ({ step, setStep }) => {
    const steps = [
        { id: 1, name: 'Job Details', icon: LayoutGridIcon },
        { id: 2, name: 'Compensation', icon: RupeeIcon }, // Using the custom Rupee Icon
        { id: 3, name: 'Requirements', icon: ListTodoIcon },
        { id: 4, name: 'Review & Publish', icon: SendIcon },
    ];

    const StepItem = ({ id, name, icon: Icon }) => {
        const isActive = id === step;
        const isCompleted = id < step;

        return (
            <div
                onClick={() => setStep(id)}
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition duration-150 font-roboto ${
                    isActive
                        ? `bg-${primaryBg} border-l-4 border-${primaryColor} text-${primaryColor} font-semibold shadow-sm`
                        : isCompleted
                        ? 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                        : 'text-gray-500 hover:bg-gray-100'
                }`}
            >
                <div className={`w-7 h-7 flex items-center justify-center rounded-full transition duration-150 ${
                    isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : isCompleted
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-200 text-gray-500'
                }`}>
                    {isCompleted ? <CheckIcon className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className="text-sm">{name}</span>
            </div>
        );
    };

    return (
        <nav className="space-y-1">
            {steps.map(s => <StepItem key={s.id} {...s} step={step} setStep={setStep} />)}
        </nav>
    );
};


/**
 * Main Application Component
 */
const App = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        title: 'Full Stack Developer',
        location: 'Pune, India',
        isRemote: false,
        description: 'Design, develop, and maintain web applications. Collaborate with product teams to define and implement new features. Ensure high quality and performance of production systems. We are looking for someone with a strong background in MERN stack.',
        salaryMin: 800000,
        salaryMax: 1500000,
        benefits: 'Annual Bonus, Comprehensive Health Coverage, Flexible Work Hours, Professional Development Budget.',
        experience: 'Mid',
        skills: 'React, NodeJS, MongoDB, Git, Agile methodologies',
        education: 'B.Tech/M.Tech',
    });
    const [isPublished, setIsPublished] = useState(false);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? e.target.checked : value,
        }));
    };

    const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

    const handleSubmit = () => {
        console.log('Final Job Posting Data:', formData);
        setIsPublished(true);
    };

    const renderStep = useMemo(() => {
        switch (step) {
            case 1:
                return <JobDetailsStep formData={formData} handleChange={handleChange} nextStep={nextStep} />;
            case 2:
                return <CompensationStep formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} />;
            case 3:
                return <RequirementsStep formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} />;
            case 4:
                return <PublishStep formData={formData} prevStep={prevStep} handleSubmit={handleSubmit} />;
            default:
                return null;
        }
    }, [step, formData]);

    if (isPublished) {
        return (
            // Full white background for success state
            <div className="min-h-screen bg-white flex items-center justify-center p-4 font-roboto">
                <div className="max-w-xl w-full bg-white p-10 rounded-xl shadow-2xl text-center space-y-6 border border-gray-100">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-8 h-8 text-green-600"/>
                    </div>
                    <h1 className="text-3xl font-poppins font-bold text-gray-800">Success! Job Published</h1>
                    <p className="text-gray-600">The position for **{formData.title}** is now live and accepting applications.</p>
                    <button onClick={() => { setIsPublished(false); setStep(1); }} className={`mt-4 bg-${primaryColor} text-white font-semibold py-2 px-6 rounded-lg hover:bg-${primaryHover} transition duration-150 shadow-md font-poppins text-sm`}>
                        Create Another Job Posting
                    </button>
                </div>
            </div>
        );
    }

    return (
        // Changed outer background to pure white (bg-white)
        <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-8 font-roboto">
            {/* Global Font Styles and Tailwind Setup */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins {
                        font-family: 'Poppins', sans-serif;
                    }
                    .font-roboto {
                        font-family: 'Roboto', sans-serif;
                    }
                `}
            </style>

            <div className="max-w-4xl w-full bg-white rounded-xl overflow-hidden md:flex border border-gray-100 shadow-xl">
                {/* Sidebar Navigation */}
                <div className="w-full md:w-64 p-5 bg-white md:bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200">
                    <h1 className="text-xl font-poppins font-bold text-gray-800 mb-4 border-b pb-3">New Job Setup</h1>
                    <Sidebar step={step} setStep={setStep} />
                </div>

                {/* Main Form Content */}
                <div className="flex-1 p-6 sm:p-8">
                    {renderStep}
                </div>
            </div>
        </div>
    );
};

export default App;